// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useEffect } from 'react';
import { Link, useNavigate } from '@/i18n/router';
import { useCart } from '@/hooks/useCart';
import CouponInput from './CouponInput';
import CartItemWithVariants from './CartItemWithVariants';
import FreeShippingBar from './FreeShippingBar';
import CartCrossSell from './CartCrossSell';
import { gaBeginCheckout } from '@/utils/analytics';
import { STORE_CONFIG } from '@/config/storeConfig';

const CartDrawer = () => {
  useStoreLanguage();
  const navigate = useNavigate();
  const {
    items,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
    isCartOpen,
    setIsCartOpen,
    appliedCoupon,
    discount,
    cartLoading,
    cartError,
    refreshCart,
    changeVariant,
  } = useCart() ?? {};

  // useEffect DEBE estar antes de cualquier return condicional
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen]);

  // Guard against missing context (robustness)
  if (!items) return null;

  if (!isCartOpen) return null;

  const subtotalAfterDiscount = totalPrice - discount;
  const shippingCost = subtotalAfterDiscount >= STORE_CONFIG.FREE_SHIPPING_THRESHOLD ? 0 : STORE_CONFIG.SHIPPING_COST_DEFAULT;
  const finalTotal = subtotalAfterDiscount + shippingCost;

  const handleCheckout = () => {
    try {
      // Tracking begin_checkout
      gaBeginCheckout({
        value: subtotalAfterDiscount,
        currency: 'EUR',
        coupon: appliedCoupon?.code,
        items: items.map((item, idx) => ({
          item_id: String(item.productId || item.bundleId || item.id),
          item_name: item.name,
          item_category: item.category,
          price: item.unitPrice,
          quantity: item.quantity,
          index: idx,
        })),
      });
    } catch {
      // silencioso
    }
    setIsCartOpen(false);
    navigate('/checkout');
  };

  const handleClearCart = () => {
    try {
      clearCart();
    } catch {
      // clear errors are silently ignored
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line text-xl text-forest-600"></i>
            </div>
            <h2 className="font-playfair text-xl font-semibold text-gray-900">{tr("Tu Carrito")}</h2>
            {tr(totalItems > 0 && (
              <span className="bg-forest-100 text-forest-700 text-sm font-medium px-2 py-0.5 rounded-full">
                {tr(totalItems)} {tr(totalItems === 1 ? 'artículo' : 'artículos')}
              </span>
            ))}
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Content */}
        {tr(cartLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
            <div className="w-10 h-10 border-3 border-forest-200 border-t-forest-600 rounded-full animate-spin mb-4"></div>
            <p className="font-inter text-sm text-gray-500">{tr("Cargando carrito...")}</p>
          </div>
        ) : cartError ? (
          <div role="alert" className="flex-1 px-6 py-12 text-sm text-red-700">{tr(cartError)} <button onClick={()=>void refreshCart()} className="font-semibold underline">{tr("Reintentar")}</button></div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
            <div className="w-20 h-20 flex items-center justify-center bg-forest-50 rounded-full mb-5">
              <i className="ri-shopping-cart-line text-3xl text-forest-400"></i>
            </div>
            <h3 className="font-playfair text-lg font-semibold text-gray-900 mb-2">{tr("Tu carrito está vacío")}</h3>
            <p className="text-gray-500 text-sm text-center mb-6 max-w-xs">{tr("Explora nuestra selección de especias y hierbas orgánicas")}</p>
            <Link
              to="/products"
              onClick={() => setIsCartOpen(false)}
              className="w-full bg-forest-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-forest-700 transition-colors cursor-pointer whitespace-nowrap text-center mb-3"
            >{tr("Ver Productos")}</Link>
            <Link
              to="/bundles"
              onClick={() => setIsCartOpen(false)}
              className="w-full flex items-center justify-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-6 py-3 rounded-lg font-medium hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-gift-2-line"></i>{tr("Ver packs con descuento")}</Link>
            {/* Enlace a ofertas activas */}
            <div className="mt-6 w-full bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
              <p className="font-inter text-xs text-amber-800 font-semibold mb-1">{tr("¿Tienes un cupón?")}</p>
              <p className="font-inter text-xs text-amber-700 mb-3">{tr("Añade productos al carrito y aplícalo antes de pagar")}</p>
              <Link
                to="/offers"
                onClick={() => setIsCartOpen(false)}
                className="inline-flex items-center gap-1.5 font-inter text-xs font-bold text-amber-800 hover:text-amber-900 transition-colors cursor-pointer"
              >
                <i className="ri-price-tag-3-line"></i>{tr("Ver ofertas disponibles")}</Link>
            </div>
          </div>
        ) : (
          <>
            {/* Items List */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {tr(items.map((item) => (
                  <CartItemWithVariants
                    key={item.id}
                    item={item}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeFromCart}
                    onChangeVariant={changeVariant}
                  />
                )))}
              </div>

              {/* Cross-sell suggestions */}
              <CartCrossSell />
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-white">
              {/* Free Shipping Progress Bar — primero, es el motivador más fuerte */}
              <FreeShippingBar subtotal={subtotalAfterDiscount} threshold={STORE_CONFIG.FREE_SHIPPING_THRESHOLD} />

              {/* Coupon Input */}
              <div className="mt-3">
                <CouponInput compact />
              </div>

              <div className="flex items-center justify-between mb-2 mt-3">
                <span className="text-gray-600 text-sm">{tr("Subtotal")}</span>
                <span className="font-medium text-gray-900">
                  {tr(totalPrice.toFixed(2).replace('.', ','))} €
                </span>
              </div>
              {tr(appliedCoupon && discount > 0 && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-emerald-600 text-sm flex items-center gap-1">
                    <i className="ri-coupon-3-line text-xs"></i>{tr("Descuento")}</span>
                  <span className="text-emerald-600 text-sm font-medium">
                    -{tr(discount.toFixed(2).replace('.', ','))} €
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-600 text-sm">{tr("Envío")}</span>
                <span className="text-forest-600 text-sm font-medium">
                  {tr(shippingCost === 0 ? 'Gratis' : `desde ${STORE_CONFIG.SHIPPING_COST_DEFAULT.toFixed(2).replace('.', ',')} €`)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-t border-gray-200 mb-2">
                <span className="font-semibold text-gray-900">{tr("Total")}</span>
                <span className="font-playfair text-xl font-bold text-forest-600">
                  {tr(finalTotal.toFixed(2).replace('.', ','))} €
                </span>
              </div>
              
              {/* Mensaje informativo de precio bloqueado */}
              <div className="flex items-center gap-1.5 text-forest-600 text-xs mb-4 bg-forest-50/50 px-3 py-2 rounded-lg">
                <i className="ri-lock-line text-sm flex-shrink-0"></i>
                <span>{tr("El precio del carrito se mantiene hasta completar el pedido")}</span>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full bg-forest-600 text-white py-3.5 rounded-lg font-medium hover:bg-forest-700 transition-colors cursor-pointer whitespace-nowrap mb-3"
              >{tr("Finalizar Compra")}</button>
              <button
                onClick={handleClearCart}
                className="w-full text-gray-500 text-sm hover:text-red-500 transition-colors cursor-pointer py-2"
              >{tr("Vaciar carrito")}</button>
            </div>
          </>
        ))}
      </div>

      {/* Inline CSS for animation */}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default CartDrawer;
